import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowUpRight,
  ArrowDownLeft,
  Search,
  RefreshCw,
  AlertCircle,
  Eye,
  Send,
  Download,
  Receipt,
  Percent,
  CheckCircle,
  Clock,
  XCircle,
  TrendingUp,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../components/ui';
import { formatCurrency, formatCompactCurrency, formatDate, formatDateTime } from '../lib/utils';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';
import { useAuth } from '../hooks/useAuth';

interface Fee {
  id: string;
  type: string;
  source: string;
  amount: number;
  description?: string;
}

interface Transaction {
  id: string;
  type: string;
  status: string;
  grossAmount: number;
  totalFees: number;
  providerFee?: number;
  netAmount: number;
  correlationId: string;
  description?: string;
  depositType?: string;
  processorId?: string;
  pixKey?: string;
  pixKeyType?: string;
  endToEndId?: string;
  customer?: { name: string; document: string; email: string } | null;
  beneficiary?: { name: string; document: string; pixKey: string; pixType: string } | null;
  paidAt?: string;
  fromAccount?: { id: string; name: string; type: string };
  toAccount?: { id: string; name: string; type: string };
  fees?: Fee[];
  createdAt: string;
  processedAt?: string;
  scheduledFor?: string;
}

interface TransactionSummary {
  totalReceived: number;
  totalReceivedConfirmed: number;
  totalPending: number;
  totalFees: number;
  totalNet: number;
  cashInPercentage: number;
  confirmedCount: number;
  pendingCount: number;
  failedCount: number;
}

interface Account {
  id: string;
  name: string;
  type: string;
  balance: number;
  pixKey?: string;
}

const transactionTypeLabels: Record<string, string> = {
  PIX_RECEIVE: 'Pix Recebido',
  PIX_SEND: 'Pix Enviado',
  TRANSFER_INTERNAL: 'Transferência Interna',
  BOLETO: 'Boleto',
  CARD_PAYMENT: 'Cartão de Crédito',
  REFUND: 'Estorno',
  SPLIT_DISTRIBUTION: 'Split',
  WITHDRAWAL: 'Saque',
  FEE_CHARGE: 'Cobrança de Taxa',
};

// Labels contextuais: quando pendente, "Pix Recebido" vira "Pix a Receber"
const getTransactionTypeLabel = (type: string, status: string): string => {
  if (type === 'PIX_RECEIVE' && status === 'PENDING') return 'Pix a Receber';
  if (type === 'PIX_RECEIVE' && status === 'FAILED') return 'Pix Expirado';
  if (type === 'PIX_RECEIVE' && status === 'CANCELLED') return 'Pix Cancelado';
  return transactionTypeLabels[type] || type;
};

const statusLabels: Record<string, string> = {
  COMPLETED: 'Confirmado',
  PENDING: 'Pendente',
  PROCESSING: 'Processando',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelado',
  REFUNDED: 'Estornado',
  SCHEDULED: 'Agendado',
};

const feeTypeLabels: Record<string, string> = {
  PIX_RECEIVE: 'Taxa Pix Recebimento',
  PIX_SEND: 'Taxa Pix Envio',
  TRANSFER: 'Taxa Transferência',
  BOLETO: 'Taxa Boleto',
  CARD: 'Taxa Cartão',
  WITHDRAWAL: 'Taxa Saque',
  MAINTENANCE: 'Taxa Manutenção',
};

const feeSourceLabels: Record<string, string> = {
  PLATFORM: 'Plataforma',
  PROVIDER: 'Provider',
};

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
  COMPLETED: 'success',
  PENDING: 'warning',
  PROCESSING: 'info',
  FAILED: 'error',
  CANCELLED: 'error',
  REFUNDED: 'warning',
  SCHEDULED: 'info',
};

const accountTypeLabels: Record<string, string> = {
  BOLSAO: 'Bolsão',
  VIRTUAL: 'Virtual',
  REAL: 'Real',
};

export function Transactions() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isPixModalOpen, setIsPixModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [pixForm, setPixForm] = useState({
    accountId: '',
    amount: '',
    pixKey: '',
    pixKeyType: 'CPF',
    description: '',
  });
  const [transferForm, setTransferForm] = useState({
    fromAccountId: '',
    toAccountId: '',
    amount: '',
    description: '',
  });

  const { isSuperAdmin, isTenantAdmin } = useAuth();
  const isAdmin = isSuperAdmin() || isTenantAdmin();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['transactions', typeFilter, statusFilter, accountTypeFilter, isAdmin],
    queryFn: async () => {
      // Sempre buscar transações internas (filtradas por tenant)
      // Não usar API do AnyPay diretamente pois traz transações de outros sistemas
      const params: Record<string, string> = { limit: '100' };
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      if (accountTypeFilter) params.accountType = accountTypeFilter;
      const response = await api.get(ENDPOINTS.transactions.list, { params });
      return response.data as { transactions: Transaction[]; total: number; summary?: TransactionSummary };
    },
  });

  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-transactions'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.accounts.list);
      return response.data as { accounts: Account[]; total: number };
    },
    enabled: !isAdmin,
  });

  const { data: feeSummaryData } = useQuery({
    queryKey: ['fee-summary'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.statements.fees);
      return response.data as { totalFees: number; feesByType: Record<string, number> };
    },
    enabled: !isAdmin,
  });

  const sendPixMutation = useMutation({
    mutationFn: async (formData: typeof pixForm) => {
      const response = await api.post(ENDPOINTS.transactions.pix.send, {
        accountId: formData.accountId,
        amount: Math.round(parseFloat(formData.amount) * 100),
        pixKey: formData.pixKey,
        pixKeyType: formData.pixKeyType,
        description: formData.description,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['fee-summary'] });
      setIsPixModalOpen(false);
      setPixForm({ accountId: '', amount: '', pixKey: '', pixKeyType: 'CPF', description: '' });
    },
  });

  const transferMutation = useMutation({
    mutationFn: async (formData: typeof transferForm) => {
      const response = await api.post(ENDPOINTS.transactions.transfer, {
        fromAccountId: formData.fromAccountId,
        toAccountId: formData.toAccountId,
        amount: Math.round(parseFloat(formData.amount) * 100),
        description: formData.description,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['fee-summary'] });
      setIsTransferModalOpen(false);
      setTransferForm({ fromAccountId: '', toAccountId: '', amount: '', description: '' });
    },
  });

  const handleExportCsv = async () => {
    try {
      const response = await api.get(ENDPOINTS.statements.export, {
        params: { format: 'csv' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `extrato-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const transactions = data?.transactions || [];
  const accounts = accountsData?.accounts || [];
  const summary = isAdmin ? (data as any)?.summary as TransactionSummary | undefined : undefined;

  const filteredTransactions = transactions.filter(tx =>
    tx.correlationId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.pixKey?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.customer?.document?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.beneficiary?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // For admin: separate by status
  const confirmedTxs = filteredTransactions.filter(tx => tx.status === 'COMPLETED');
  const pendingTxs = filteredTransactions.filter(tx => tx.status === 'PENDING');
  const failedTxs = filteredTransactions.filter(tx => tx.status === 'FAILED');

  // For correntista: client-side stats
  const clientTotalReceived = !isAdmin ? transactions
    .filter(tx => ['PIX_RECEIVE', 'BOLETO', 'CARD_PAYMENT'].includes(tx.type) && tx.status === 'COMPLETED')
    .reduce((sum, tx) => sum + Number(tx.grossAmount || tx.netAmount), 0) : 0;

  const clientTotalSent = !isAdmin ? transactions
    .filter(tx => ['PIX_SEND', 'TRANSFER_INTERNAL', 'WITHDRAWAL'].includes(tx.type) && tx.status === 'COMPLETED')
    .reduce((sum, tx) => sum + Number(tx.grossAmount || tx.netAmount), 0) : 0;

  const clientTotalFees = !isAdmin ? transactions
    .filter(tx => tx.status === 'COMPLETED')
    .reduce((sum, tx) => sum + Number(tx.totalFees || 0), 0) : 0;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar transações</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  // Render a single transaction row
  const renderTransactionRow = (tx: Transaction) => {
    const isIncoming = ['PIX_RECEIVE', 'BOLETO', 'CARD_PAYMENT'].includes(tx.type);
    const amount = tx.grossAmount || tx.netAmount;
    const hasFees = tx.totalFees && tx.totalFees > 0;
    // Customer/beneficiary name from AtivoPay data
    const personName = tx.customer?.name || tx.beneficiary?.name || null;
    return (
      <div
        key={tx.id}
        className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors cursor-pointer"
        onClick={() => setSelectedTransaction(tx)}
      >
        <div className="flex items-center gap-4">
          <div className={`p-2 rounded-lg ${
            isIncoming ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}>
            {isIncoming ? <ArrowDownLeft className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
          </div>
          <div>
            <p className="font-medium text-surface-100">
              {getTransactionTypeLabel(tx.type, tx.status)}
            </p>
            {personName ? (
              <p className="text-sm text-surface-300">{personName}</p>
            ) : null}
            <p className="text-sm text-surface-500">
              {tx.description || (tx.correlationId ? `${tx.correlationId.slice(0, 20)}...` : '-')}
            </p>
            {tx.fromAccount && (
              <p className="text-xs text-surface-500">
                De: {tx.fromAccount.name}
                <Badge variant="info" className="ml-1 text-xs">{accountTypeLabels[tx.fromAccount.type]}</Badge>
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className={`font-semibold ${isIncoming ? 'text-green-400' : 'text-red-400'}`}>
              {isIncoming ? '+' : '-'}{formatCurrency(Number(amount))}
            </p>
            {hasFees && (
              <p className="text-xs text-yellow-400 flex items-center justify-end gap-1">
                <Receipt className="w-3 h-3" />
                Taxa: {formatCurrency(Number(tx.totalFees))}
              </p>
            )}
            {hasFees && tx.netAmount > 0 && (
              <p className="text-xs text-surface-400">
                Líquido: {formatCurrency(Number(tx.netAmount))}
              </p>
            )}
            <p className="text-xs text-surface-500">
              {tx.paidAt ? formatDate(tx.paidAt) : formatDate(tx.createdAt)}
            </p>
          </div>
          <Badge variant={statusColors[tx.status] || 'info'}>
            {statusLabels[tx.status] || tx.status}
          </Badge>
          <Button variant="ghost" size="sm">
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">
            {isAdmin ? 'Transações do Gateway' : 'Transações'}
          </h1>
          <p className="text-surface-400">
            {isAdmin
              ? 'Movimentações via AnyPayBR — dados em tempo real'
              : 'Histórico de movimentações financeiras'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!isAdmin && (
            <Button variant="ghost" onClick={handleExportCsv} title="Exportar CSV">
              <Download className="w-4 h-4" />
            </Button>
          )}
          {!isAdmin && (
            <>
              <Button variant="secondary" onClick={() => setIsTransferModalOpen(true)}>
                <ArrowUpRight className="w-4 h-4 mr-2" />
                Transferir
              </Button>
              <Button variant="primary" onClick={() => setIsPixModalOpen(true)}>
                <Send className="w-4 h-4 mr-2" />
                Enviar Pix
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Admin Stats — from backend summary */}
      {isAdmin && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400 flex items-center gap-1">
                <CheckCircle className="w-3 h-3 flex-shrink-0" /> Recebido (Pago)
              </p>
              <p className="text-xl sm:text-2xl font-bold text-green-400 mt-1" title={formatCurrency(summary.totalReceivedConfirmed)}>
                {formatCompactCurrency(summary.totalReceivedConfirmed)}
              </p>
              <p className="text-xs text-surface-500 mt-1">{summary.confirmedCount} transações</p>
            </CardBody>
          </Card>
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400 flex items-center gap-1">
                <Clock className="w-3 h-3 flex-shrink-0" /> Pendente
              </p>
              <p className="text-xl sm:text-2xl font-bold text-orange-400 mt-1" title={formatCurrency(summary.totalPending)}>
                {formatCompactCurrency(summary.totalPending)}
              </p>
              <p className="text-xs text-surface-500 mt-1">{summary.pendingCount} transações</p>
            </CardBody>
          </Card>
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400 flex items-center gap-1">
                <Percent className="w-3 h-3 flex-shrink-0" /> Taxas ({(summary.cashInPercentage * 100).toFixed(0)}%)
              </p>
              <p className="text-xl sm:text-2xl font-bold text-yellow-400 mt-1" title={formatCurrency(summary.totalFees)}>
                {formatCompactCurrency(summary.totalFees)}
              </p>
              <p className="text-xs text-surface-500 mt-1">sobre confirmados</p>
            </CardBody>
          </Card>
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400 flex items-center gap-1">
                <TrendingUp className="w-3 h-3 flex-shrink-0" /> Líquido
              </p>
              <p className="text-xl sm:text-2xl font-bold text-blue-400 mt-1" title={formatCurrency(summary.totalNet)}>
                {formatCompactCurrency(summary.totalNet)}
              </p>
              <p className="text-xs text-surface-500 mt-1">após taxas</p>
            </CardBody>
          </Card>
          {isSuperAdmin() && (
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400 flex items-center gap-1">
                <ArrowUpRight className="w-3 h-3 flex-shrink-0" /> Saldo Escrow
              </p>
              <p className="text-xl sm:text-2xl font-bold text-purple-400 mt-1" title={formatCurrency((summary as any).escrowBalance)}>
                {formatCompactCurrency((summary as any).escrowBalance)}
              </p>
              <p className="text-xs text-surface-500 mt-1">disponível no gateway</p>
            </CardBody>
          </Card>
          )}
        </div>
      )}

      {/* Correntista Stats */}
      {!isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400">Total de Transações</p>
              <p className="text-lg lg:text-xl font-bold text-surface-100 mt-1">
                {isLoading ? '...' : transactions.length}
              </p>
            </CardBody>
          </Card>
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400">Total Recebido</p>
              <p className="text-lg lg:text-xl font-bold text-green-400 mt-1 truncate" title={!isLoading ? formatCurrency(clientTotalReceived) : ''}>
                {isLoading ? '...' : formatCompactCurrency(clientTotalReceived)}
              </p>
            </CardBody>
          </Card>
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400">Total Enviado</p>
              <p className="text-lg lg:text-xl font-bold text-red-400 mt-1 truncate" title={!isLoading ? formatCurrency(clientTotalSent) : ''}>
                {isLoading ? '...' : formatCompactCurrency(clientTotalSent)}
              </p>
            </CardBody>
          </Card>
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400 flex items-center gap-1">
                <Percent className="w-3 h-3 flex-shrink-0" /> Total em Taxas
              </p>
              <p className="text-lg lg:text-xl font-bold text-yellow-400 mt-1 truncate" title={!isLoading ? formatCurrency(feeSummaryData?.totalFees || clientTotalFees) : ''}>
                {isLoading ? '...' : formatCompactCurrency(feeSummaryData?.totalFees || clientTotalFees)}
              </p>
            </CardBody>
          </Card>
          <Card variant="stat">
            <CardBody className="min-w-0">
              <p className="text-sm text-surface-400">Pendentes</p>
              <p className="text-lg lg:text-xl font-bold text-orange-400 mt-1">
                {transactions.filter(tx => tx.status === 'PENDING').length}
              </p>
            </CardBody>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
            <div className="md:col-span-5">
              <label className="block text-xs font-medium text-surface-400 mb-1.5">Buscar transação</label>
              <Input
                placeholder="Nome, descrição, chave Pix ou ID..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {!isAdmin && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-surface-400 mb-1.5">Tipo de transação</label>
                <Select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  options={[
                    { value: '', label: 'Todos os tipos' },
                    { value: 'PIX_RECEIVE', label: 'Pix Recebido' },
                    { value: 'PIX_SEND', label: 'Pix Enviado' },
                    { value: 'TRANSFER_INTERNAL', label: 'Transferência' },
                    { value: 'BOLETO', label: 'Boleto' },
                    { value: 'CARD_PAYMENT', label: 'Cartão' },
                  ]}
                />
              </div>
            )}
            <div className={!isAdmin ? "md:col-span-3" : "md:col-span-4"}>
              <label className="block text-xs font-medium text-surface-400 mb-1.5">Status</label>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: '', label: 'Todos os status' },
                  { value: 'COMPLETED', label: 'Confirmado' },
                  { value: 'PENDING', label: 'Pendente' },
                  ...(isAdmin ? [] : [{ value: 'PROCESSING', label: 'Processando' }]),
                  { value: 'FAILED', label: 'Falhou' },
                ]}
              />
            </div>
            {!isAdmin && (
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-surface-400 mb-1.5">Tipo de conta</label>
                <Select
                  value={accountTypeFilter}
                  onChange={(e) => setAccountTypeFilter(e.target.value)}
                  options={[
                    { value: '', label: 'Todas as contas' },
                    { value: 'BOLSAO', label: 'Bolsão' },
                    { value: 'VIRTUAL', label: 'Virtual' },
                    { value: 'REAL', label: 'Real' },
                  ]}
                />
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Admin: Grouped transaction lists */}
      {isAdmin && !isLoading && (
        <>
          {/* Confirmed Transactions */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Confirmadas
                <Badge variant="success">{confirmedTxs.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardBody className="p-0">
              {confirmedTxs.length === 0 ? (
                <div className="text-center py-8 text-surface-400">
                  <p>Nenhuma transação confirmada</p>
                </div>
              ) : (
                <div className="divide-y divide-surface-800">
                  {confirmedTxs.map(renderTransactionRow)}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Pending Transactions */}
          {pendingTxs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-400" />
                  Pendentes
                  <Badge variant="warning">{pendingTxs.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <div className="divide-y divide-surface-800">
                  {pendingTxs.map(renderTransactionRow)}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Failed Transactions */}
          {failedTxs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  Falhas
                  <Badge variant="error">{failedTxs.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardBody className="p-0">
                <div className="divide-y divide-surface-800">
                  {failedTxs.map(renderTransactionRow)}
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}

      {/* Admin loading state */}
      {isAdmin && isLoading && (
        <Card>
          <CardBody>
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-16" />
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Correntista: Single transaction list */}
      {!isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ArrowUpRight className="w-5 h-5 text-primary-400" />
              Lista de Transações
              <Badge variant="info">{filteredTransactions.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-4">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="animate-pulse bg-surface-800 rounded h-16" />
                ))}
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-surface-400">
                <ArrowUpRight className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nenhuma transação encontrada</p>
              </div>
            ) : (
              <div className="divide-y divide-surface-800">
                {filteredTransactions.map(renderTransactionRow)}
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Send Pix Modal (Correntista only) */}
      {!isAdmin && (
        <Modal
          isOpen={isPixModalOpen}
          onClose={() => setIsPixModalOpen(false)}
          title="Enviar Pix"
        >
          <form onSubmit={(e) => { e.preventDefault(); sendPixMutation.mutate(pixForm); }} className="space-y-4">
            <Select
              label="Conta de Origem"
              value={pixForm.accountId}
              onChange={(e) => setPixForm({ ...pixForm, accountId: e.target.value })}
              options={[
                { value: '', label: 'Selecione uma conta' },
                ...accounts.map(a => ({
                  value: a.id,
                  label: `${a.name} - ${formatCurrency(Number(a.balance))}`,
                })),
              ]}
              required
            />
            <Input
              label="Valor (R$)"
              type="number"
              step="0.01"
              min="0.01"
              value={pixForm.amount}
              onChange={(e) => setPixForm({ ...pixForm, amount: e.target.value })}
              required
            />
            <Select
              label="Tipo de Chave Pix"
              value={pixForm.pixKeyType}
              onChange={(e) => setPixForm({ ...pixForm, pixKeyType: e.target.value })}
              options={[
                { value: 'CPF', label: 'CPF' },
                { value: 'CNPJ', label: 'CNPJ' },
                { value: 'EMAIL', label: 'Email' },
                { value: 'PHONE', label: 'Telefone' },
                { value: 'RANDOM', label: 'Chave Aleatória' },
              ]}
            />
            <Input
              label="Chave Pix de Destino"
              value={pixForm.pixKey}
              onChange={(e) => setPixForm({ ...pixForm, pixKey: e.target.value })}
              required
            />
            <Input
              label="Descrição (opcional)"
              value={pixForm.description}
              onChange={(e) => setPixForm({ ...pixForm, description: e.target.value })}
            />
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setIsPixModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" isLoading={sendPixMutation.isPending}>
                Enviar Pix
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Transfer Modal (Correntista only) */}
      {!isAdmin && (
        <Modal
          isOpen={isTransferModalOpen}
          onClose={() => setIsTransferModalOpen(false)}
          title="Transferência Interna"
        >
          <form onSubmit={(e) => { e.preventDefault(); transferMutation.mutate(transferForm); }} className="space-y-4">
            <Select
              label="Conta de Origem"
              value={transferForm.fromAccountId}
              onChange={(e) => setTransferForm({ ...transferForm, fromAccountId: e.target.value })}
              options={[
                { value: '', label: 'Selecione uma conta' },
                ...accounts.map(a => ({
                  value: a.id,
                  label: `${a.name} - ${formatCurrency(Number(a.balance))}`,
                })),
              ]}
              required
            />
            <Select
              label="Conta de Destino"
              value={transferForm.toAccountId}
              onChange={(e) => setTransferForm({ ...transferForm, toAccountId: e.target.value })}
              options={[
                { value: '', label: 'Selecione uma conta' },
                ...accounts.filter(a => a.id !== transferForm.fromAccountId).map(a => ({
                  value: a.id,
                  label: `${a.name} - ${formatCurrency(Number(a.balance))}`,
                })),
              ]}
              required
            />
            <Input
              label="Valor (R$)"
              type="number"
              step="0.01"
              min="0.01"
              value={transferForm.amount}
              onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
              required
            />
            <Input
              label="Descrição (opcional)"
              value={transferForm.description}
              onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
            />
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="secondary" onClick={() => setIsTransferModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" isLoading={transferMutation.isPending}>
                Transferir
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Transaction Details Modal */}
      <Modal
        isOpen={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        title="Detalhes da Transação"
        size="lg"
      >
        {selectedTransaction && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-surface-500">Tipo</p>
                <p className="text-surface-100">{getTransactionTypeLabel(selectedTransaction.type, selectedTransaction.status)}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500">Status</p>
                <Badge variant={statusColors[selectedTransaction.status]}>
                  {statusLabels[selectedTransaction.status] || selectedTransaction.status}
                </Badge>
              </div>
            </div>

            {/* Amount breakdown */}
            <div className="p-4 bg-surface-800 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-surface-400">Valor Bruto</span>
                <span className="text-surface-100 font-semibold">
                  {formatCurrency(Number(selectedTransaction.grossAmount || selectedTransaction.netAmount))}
                </span>
              </div>
              {selectedTransaction.totalFees > 0 && (
                <div className="flex justify-between text-yellow-400">
                  <span>Total de Taxas</span>
                  <span>- {formatCurrency(Number(selectedTransaction.totalFees))}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-surface-700 pt-2">
                <span className="text-surface-300 font-medium">Valor Líquido</span>
                <span className="text-surface-100 font-bold text-lg">
                  {formatCurrency(Number(selectedTransaction.netAmount))}
                </span>
              </div>
            </div>

            {/* Fee breakdown */}
            {selectedTransaction.fees && selectedTransaction.fees.length > 0 && (
              <div className="p-4 bg-surface-800/50 rounded-lg">
                <p className="text-sm text-surface-400 mb-2 flex items-center gap-1">
                  <Receipt className="w-4 h-4" /> Detalhamento de Taxas
                </p>
                <div className="space-y-1">
                  {selectedTransaction.fees.map((fee, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-surface-300">
                        {feeTypeLabels[fee.type] || fee.type}
                        <Badge variant="info" className="ml-2 text-xs">
                          {feeSourceLabels[fee.source] || fee.source}
                        </Badge>
                      </span>
                      <span className="text-yellow-400">{formatCurrency(Number(fee.amount))}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <p className="text-sm text-surface-500">ID da Transação</p>
                <p className="text-surface-100 font-mono text-sm break-all">{selectedTransaction.correlationId}</p>
              </div>
              {/* Customer info (cash_in) */}
              {selectedTransaction.customer && (
                <div className="col-span-2 p-3 bg-surface-800/50 rounded-lg">
                  <p className="text-sm text-surface-400 mb-1 flex items-center gap-1">
                    <ArrowDownLeft className="w-3 h-3" /> Cliente (Pagador)
                  </p>
                  <p className="text-surface-100 font-medium">{selectedTransaction.customer.name}</p>
                  {selectedTransaction.customer.document && (
                    <p className="text-xs text-surface-400">CPF/CNPJ: {selectedTransaction.customer.document}</p>
                  )}
                  {selectedTransaction.customer.email && selectedTransaction.customer.email !== '00000000000' && (
                    <p className="text-xs text-surface-400">{selectedTransaction.customer.email}</p>
                  )}
                </div>
              )}
              {/* Beneficiary info (cash_out) */}
              {selectedTransaction.beneficiary && (
                <div className="col-span-2 p-3 bg-surface-800/50 rounded-lg">
                  <p className="text-sm text-surface-400 mb-1 flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3" /> Beneficiário (Saque)
                  </p>
                  <p className="text-surface-100 font-medium">{selectedTransaction.beneficiary.name}</p>
                  {selectedTransaction.beneficiary.document && (
                    <p className="text-xs text-surface-400">CPF/CNPJ: {selectedTransaction.beneficiary.document}</p>
                  )}
                  {selectedTransaction.beneficiary.pixKey && (
                    <p className="text-xs text-surface-400">Chave PIX: {selectedTransaction.beneficiary.pixKey} ({selectedTransaction.beneficiary.pixType})</p>
                  )}
                </div>
              )}
              {selectedTransaction.depositType && (
                <div>
                  <p className="text-sm text-surface-500">Tipo de Depósito</p>
                  <p className="text-surface-100 text-sm">{selectedTransaction.depositType}</p>
                </div>
              )}
              {selectedTransaction.processorId && (
                <div>
                  <p className="text-sm text-surface-500">Processador</p>
                  <p className="text-surface-100 text-sm">{selectedTransaction.processorId}</p>
                </div>
              )}
              {selectedTransaction.pixKey && (
                <>
                  <div>
                    <p className="text-sm text-surface-500">Chave Pix</p>
                    <p className="text-surface-100 font-mono text-sm">{selectedTransaction.pixKey}</p>
                  </div>
                  <div>
                    <p className="text-sm text-surface-500">Tipo da Chave</p>
                    <p className="text-surface-100">{selectedTransaction.pixKeyType}</p>
                  </div>
                </>
              )}
              {selectedTransaction.endToEndId && (
                <div className="col-span-2">
                  <p className="text-sm text-surface-500">End-to-End ID</p>
                  <p className="text-surface-100 font-mono text-sm break-all">{selectedTransaction.endToEndId}</p>
                </div>
              )}
              {selectedTransaction.fromAccount && (
                <div>
                  <p className="text-sm text-surface-500">Conta de Origem</p>
                  <p className="text-surface-100">{selectedTransaction.fromAccount.name}</p>
                  <Badge variant="info" className="text-xs">{accountTypeLabels[selectedTransaction.fromAccount.type]}</Badge>
                </div>
              )}
              {selectedTransaction.toAccount && (
                <div>
                  <p className="text-sm text-surface-500">Conta de Destino</p>
                  <p className="text-surface-100">{selectedTransaction.toAccount.name}</p>
                  <Badge variant="info" className="text-xs">{accountTypeLabels[selectedTransaction.toAccount.type]}</Badge>
                </div>
              )}
              <div>
                <p className="text-sm text-surface-500">Criada em</p>
                <p className="text-surface-100">{formatDateTime(selectedTransaction.createdAt)}</p>
              </div>
              {selectedTransaction.paidAt && (
                <div>
                  <p className="text-sm text-surface-500">Pago em</p>
                  <p className="text-surface-100 text-green-400">{formatDateTime(selectedTransaction.paidAt)}</p>
                </div>
              )}
              {!selectedTransaction.paidAt && selectedTransaction.processedAt && (
                <div>
                  <p className="text-sm text-surface-500">Processada em</p>
                  <p className="text-surface-100">{formatDateTime(selectedTransaction.processedAt)}</p>
                </div>
              )}
            </div>
            {selectedTransaction.description && (
              <div>
                <p className="text-sm text-surface-500">Descrição</p>
                <p className="text-surface-100">{selectedTransaction.description}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </motion.div>
  );
}